import * as React from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';
import { BaseInput } from './inputs/baseInput';
import { DatePickerInput } from './inputs/datePickerInputs';
import { EmailInput } from './inputs/emailInput';
import { FileInput } from './inputs/fileInput';
import { MediaInput } from './inputs/mediaInput';
import { NumberInput } from './inputs/numberInput';
import { PasswordInput } from './inputs/passwordInput';
import { PhoneInput } from './inputs/phoneInput';
import { TextAreaInput } from './inputs/textAreaInput';

type InputType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'phone'
  | 'textArea'
  | 'media'
  | 'file'
  | 'date'
  | 'markdown';

interface InputProps<T extends FieldValues> extends React.ComponentProps<'input'> {
  label?: React.ReactNode;
  placeholder?: string;
  helperText?: string;
  type: InputType;
  disabled?: boolean;
  leftIcon?: React.ReactNode;
  error?: string;
  setValue?: UseFormSetValue<T>;
  rows?: number;
  multiple?: boolean;
  imageValue?: File[];
  fileValue?: File;
  accept?: string;
  // eslint-disable-next-line no-unused-vars
  validateFileName?: (fileName: string) => Promise<{ error?: string; isValid: boolean }>;
  fileNameValidationMessage?: string;
  checkDuplicates?: boolean;
  // eslint-disable-next-line no-unused-vars
  setDuplicates?: (duplicates: string[]) => void;
  duplicateFileNames?: string[];
  inputSize?: 'sm' | 'md' | 'lg';
}

function Input<T extends FieldValues>({
  type = 'text',
  label,
  placeholder,
  helperText,
  disabled = false,
  leftIcon,
  error,
  rows,
  imageValue,
  fileValue,
  accept,
  setValue,
  multiple,
  validateFileName,
  fileNameValidationMessage,
  value,
  checkDuplicates,
  setDuplicates,
  duplicateFileNames,
  inputSize = 'md',
  ...props
}: InputProps<T>) {
  const fieldName = (props as { name?: string })?.name;

  function getInputComponent() {
    switch (type) {
      case 'media':
        return (
          <MediaInput
            setValue={setValue!}
            error={error}
            name={fieldName as string}
            multiple={multiple}
            value={imageValue || (Array.isArray(value) ? (value as File[]) : [])}
            validateFileName={validateFileName}
            fileNameValidationMessage={fileNameValidationMessage}
            checkDuplicates={checkDuplicates}
            setDuplicates={setDuplicates}
            duplicateFileNames={duplicateFileNames}
            accept={accept}
            disabled={disabled}
            {...props}
          />
        );
      case 'text':
        return (
          <BaseInput
            leftIcon={leftIcon}
            disabled={disabled}
            placeholder={placeholder || ''}
            helperText={helperText}
            error={error}
            inputSize={inputSize}
            value={value}
            {...props}
          />
        );
      case 'number':
        return (
          <NumberInput
            leftIcon={leftIcon}
            disabled={disabled}
            placeholder={placeholder || ''}
            helperText={helperText}
            error={error}
            setValue={setValue!}
            name={fieldName as string}
            {...props}
          />
        );
      case 'email':
        return (
          <EmailInput
            leftIcon={leftIcon}
            disabled={disabled}
            placeholder={placeholder || ''}
            helperText={helperText}
            error={error}
            {...props}
          />
        );
      case 'phone':
        return (
          <PhoneInput
            disabled={disabled}
            placeholder={placeholder || ''}
            helperText={helperText}
            {...props}
          />
        );
      case 'textArea':
        return (
          <TextAreaInput
            placeholder={placeholder || ''}
            helperText={helperText}
            disabled={disabled}
            error={error}
            rows={rows}
            value={value as string}
            {...(props as React.ComponentProps<'textarea'>)}
          />
        );
      case 'password':
        return (
          <PasswordInput
            placeholder={placeholder || ''}
            helperText={helperText}
            disabled={disabled}
            error={error}
            {...props}
          />
        );
      case 'file':
        return (
          <FileInput
            setValue={setValue!}
            name={fieldName as string}
            error={error}
            accept={accept}
            value={fileValue || (value instanceof File ? value : undefined)}
            placeholder={placeholder || ''}
            helperText={helperText}
            disabled={disabled}
          />
        );
      case 'date':
        return (
          <DatePickerInput
            placeholder={placeholder || ''}
            disabled={disabled}
            error={error}
            setValue={setValue!}
            name={fieldName as string}
            {...props}
          />
        );
    }
  }

  return (
    <div className="self-stretch inline-flex flex-col justify-start items-start gap-1.5 w-full">
      {label && (
        <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
          <div className="justify-start text-gray-700 text-sm font-medium leading-tight w-full">
            {label}
          </div>
        </div>
      )}
      {getInputComponent()}
    </div>
  );
}

export { Input };
